/**
 * @file 向随心搭注册编辑器相关方法
 * @document https://ku.baidu-int.com/knowledge/HFVrC7hq1Q/2tsPs8CtSd/OwQGfaBCAW/At8b46mFJMUNhv
 */

import {getMorphoService, serviceNames} from '@/rte/editor';
import {SetupBridge} from '@baidu/code-live-render';
import {findNodeById, getEditor, findNodesByType} from './utils';
import {getCurrentCard} from './getCurrentCard';
import {Path} from './typings';
import {getInsertMethod, insertRow} from './insert';


const injectMorphoAPI = (bridge: SetupBridge, builderId: string) => {
    bridge.injectApi('Ku.editor.getByType', (type: string, range?: any) => {
        const editor = getEditor();
        if (!editor) {
            return [];
        }
        let findRange = editor.children;
        if (typeof range === 'string') {
            // 范围通过 id 传入
            const rangeNode = findNodeById(range, editor.children, []);
            if (rangeNode) {
                findRange = rangeNode[0].children;
            }
        }
        else if (range && range[0] && range[0].children) {
            findRange = range[0].children;
        }
        return findNodesByType(type, findRange);
    });

    bridge.injectApi('Ku.editor.getById', (id: string) => {
        const editor = getEditor();
        if (!editor) {
            return null;
        }
        return findNodeById(id, editor.children, []);
    });

    bridge.injectApi('Ku.editor.getCurrentCard', () => {
        return getCurrentCard(builderId);
    });

    bridge.injectApi('Ku.editor.set', (attributes: any, at: Path | string, childIndex?: number) => {
        const editor = getEditor();
        if (!editor) {
            return;
        }
        const transformService = getMorphoService(serviceNames.ITransformService);
        let path: Path = [];
        if (typeof at === 'string') {
            const atNode = findNodeById(at, editor.children, []);
            if (atNode?.[1]) {
                path = atNode[1];
            }
        }
        else {
            path = at;
        }
        if (!path) {
            return;
        }
        if (typeof childIndex === 'number') {
            path = path.concat(childIndex);
            transformService.setNodes(attributes, path);
        }
    });

    // 插入结点
    bridge.injectApi('Ku.editor.insert', (
        // 插入类型
        type: string,
        at: Path | 'start' | 'end' | 'before' | 'after' | string,
        options?: {
            // 添加到结点上的属性
            attributes?: any;
            // 结点的内容，可以为字符串，也可以为结点数组
            content?: string | any[];
        }
    ) => {
        return getInsertMethod(builderId)(type, at, options);
    });

    // 表格中插入一行
    bridge.injectApi('Ku.editor.insertRow', insertRow);

    bridge.injectApi('Ku.editor.serialize', (options: any) => {
        const editor = getEditor();
        if (!editor) {
            return '';
        }
        const customOptions = options.customOptions || {};
        const content = options.content || editor.children.slice(0);
        switch (options.format) {
            case 'plainText':
                return editor.mdSerializer.serialize(content, {editor, ...customOptions});
            case 'htmlForLLM':
                return editor.htmlSerializer.serialize(content, {
                    editor, forLLM: true, ...customOptions,
                });
            case 'markdown':
                return editor.mdSerializer.serialize(content, {
                    editor, standard: true, ...customOptions,
                });
            case 'html':
            default:
                return editor.htmlSerializer.serialize(content, {editor, ...customOptions});
        }
    });
};

export {injectMorphoAPI};
